import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Download, FileUp, LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import * as Mutation from '../../../api/gql/mutation'
import { useUpload } from '../../../components/file-manager/hooks/useUpload'
import { costBudgetDownloadUrl } from '../lib/download'
import { describeCostControlError } from '../lib/errors'
import { importFileProblem } from '../lib/importSource'
import type { CostImportError, CostPriceImportItem, CostPriceImportResult } from '../types'

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
const mimeOf = (file: File) => file.type || (file.name.toLowerCase().endsWith('.csv') ? 'text/csv' : XLSX_MIME)

interface Props {
  contractId: string
  projectId: string
  disabled: boolean
  onRows: (rows: CostPriceImportItem[]) => void
  onErrors: (errors: CostImportError[]) => void
}

/**
 * Freelo #33146759: "Import z Excelu" in the price editor. The file is uploaded like a budget import, read by the
 * server (readCostPriceItemsFile writes nothing) and deleted again; the rows go to the editor's draft.
 */
const PriceFileImport: React.FC<Props> = ({ contractId, projectId, disabled, onRows, onErrors }) => {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [createFile] = useMutation(Mutation.CREATE_FILE, { context: { handlesErrors: true } })
  const [setFileUploaded] = useMutation(Mutation.SET_FILE_UPLOADED, { context: { handlesErrors: true } })
  const [deleteFile] = useMutation(Mutation.DELETE_FILE, { context: { handlesErrors: true } })
  const [readFile] = useMutation(Mutation.READ_COST_PRICE_ITEMS_FILE, { context: { handlesErrors: true } })
  const [upload] = useUpload()

  const run = async (file: File) => {
    const problem = importFileProblem(file.name, file.size)
    if (problem) {
      toastr.error(t('Common_error'), t(problem))
      return
    }
    setBusy(true)
    let fileId: string | null = null
    try {
      const { data } = await createFile({ variables: { input: { filename: file.name, size: file.size, mimetype: mimeOf(file), encoding: 'utf8', directory: 'cost-control' } } })
      fileId = data.createFile.file.id as string
      // The URL is signed for mimeOf(file); an empty file.type would make the upload send another type and fail.
      const body = file.type ? file : new File([file], file.name, { type: mimeOf(file) })
      const uploaded = await upload([{ file: body, id: fileId, name: file.name, signedUrl: data.createFile.uploadUrl }])
      if (!Array.isArray(uploaded)) return
      await setFileUploaded({ variables: { ids: uploaded } })
      const res = await readFile({ variables: { id: contractId, fileId } })
      const result: CostPriceImportResult = res.data.readCostPriceItemsFile
      if (result.errors.length) onErrors(result.errors)
      else if (!result.items.length) toastr.warning('', t('CostControl_price_file_empty'))
      else onRows(result.items)
    } catch (e: any) {
      toastr.error(t('Common_error'), describeCostControlError(e?.message, t))
    } finally {
      if (fileId) {
        try { await deleteFile({ variables: { ids: [fileId] } }) } catch (e) { console.warn('Failed to clean up a price import file', e) }
      }
      setBusy(false)
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.csv"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          if (file) void run(file)
        }}
      />
      <Button variant="outline" size="sm" className="h-8" onClick={() => inputRef.current?.click()} disabled={disabled || busy}>
        {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
        {t('CostControl_price_file_import')}
      </Button>
      <Button variant="link" size="sm" className="h-8 px-0" onClick={() => window.open(costBudgetDownloadUrl(projectId, 'price-template'), '_blank')}>
        <Download className="h-4 w-4" />
        {t('CostControl_price_file_template')}
      </Button>
    </>
  )
}
export default PriceFileImport
